// Central list of backend endpoint paths. Feature hooks SHOULD import from
// here to make the blast radius of a rename obvious. Populated lazily as
// features migrate — not every endpoint has to live here on day 1.

export const API = {
  services: '/api/services',
  // Migration 063 — public per-product detail (powers `#services/<slug>`).
  serviceBySlug: (slug: string) => `/api/services/${encodeURIComponent(slug)}`,
  me: '/api/me',
  testimonials: '/api/testimonials',
  myBookings: '/api/my-bookings',
  myBookingResources: (id: number | string) => `/api/my-bookings/${id}/resources`,
  myPurchases: '/api/my-purchases',
  bookingsUnavailable: '/api/bookings/unavailable',
  bookings: '/api/bookings',
  paypalConfig: '/api/config/paypal',
  paypalCreateOrder: '/api/paypal/create-order',
  paypalCaptureOrder: '/api/paypal/capture-order',
  contributionCreateOrder: '/api/contributions/create-order',
  contributionCaptureOrder: '/api/contributions/capture-order',
  // Plan 122 — anonymous gift-certificate purchase.
  giftCertificateCreateOrder: '/api/gift-certificates/create-order',
  giftCertificateCaptureOrder: '/api/gift-certificates/capture-order',
  freeCheckout: '/api/bookings/free-checkout',
  couponsValidate: '/api/coupons/validate',
  purchaseComplete: (id: number | string) => `/api/purchases/${id}/complete`,
  questionnaire: '/api/questionnaire',
  userProfile: '/api/user/profile',
  birthDataRequest: '/api/user/birth-data-request',
  authLogin: '/api/auth/login',
  authRegister: '/api/auth/register',
  authSendVerification: '/api/auth/send-verification',
  authPasswordResetRequest: '/api/auth/password-reset/request',
  authPasswordResetConfirm: '/api/auth/password-reset/confirm',
  contact: '/api/contact',
  googleAuthUrl: '/api/auth/google/url',
  // admin
  adminUsers: '/api/admin/users',
  adminUser: (id: number) => `/api/admin/users/${id}`,
  adminUserNotes: (id: number) => `/api/admin/users/${id}/notes`,
  adminUserQuestionnaires: (id: number) => `/api/admin/users/${id}/questionnaires`,
  // Plan 70 — admin client detail.
  adminClientContacts: (id: number) => `/api/admin/clients/${id}/contacts`,
  adminClientSessions: (id: number) => `/api/admin/clients/${id}/sessions`,
  adminBookingResources: (bookingId: number) => `/api/admin/bookings/${bookingId}/resources`,
  adminBookingResource: (bookingId: number, resourceId: number) =>
    `/api/admin/bookings/${bookingId}/resources/${resourceId}`,
  adminBirthRequests: '/api/admin/birth-data-requests',
  adminBirthRequestApprove: (id: number) => `/api/admin/birth-data-requests/${id}/approve`,
  adminBirthRequestReject: (id: number) => `/api/admin/birth-data-requests/${id}/reject`,
  adminCoupons: '/api/admin/coupons',
  adminCoupon: (id: number) => `/api/admin/coupons/${id}`,
  adminCouponRedemptions: (id: number) => `/api/admin/coupons/${id}/redemptions`,
  // Plan 120 — Network, Referrals & Recommendations.
  partnerLinks: '/api/partner-links',
  adminPartnerLinks: '/api/admin/partner-links',
  adminPartnerLink: (id: number) => `/api/admin/partner-links/${id}`,
  adminPartnerLinkReorder: (id: number) => `/api/admin/partner-links/${id}/reorder`,
  // Plan 121 — blog.
  blogPosts: '/api/blog/posts',
  blogPost: (slug: string) => `/api/blog/posts/${slug}`,
  adminBlogPosts: '/api/admin/blog',
  adminBlogPost: (id: number) => `/api/admin/blog/${id}`,
  adminBlogImages: '/api/admin/blog/images',
  // Content Studio — editorial backlog + kanban (migration 069).
  adminContentIdeas: '/api/admin/content-studio',
  adminContentIdea: (id: number) => `/api/admin/content-studio/${id}`,
  adminContentIdeaPromote: (id: number) => `/api/admin/content-studio/${id}/promote`,
  // Migration 063 — admin Products & Services manager.
  adminServices: '/api/admin/services',
  adminService: (id: number) => `/api/admin/services/${id}`,
  adminServiceImages: '/api/admin/services/images',
  adminServiceReorder: '/api/admin/services/reorder',
  adminTestimonials: '/api/admin/testimonials',
  adminTestimonial: (id: number) => `/api/admin/testimonials/${id}`,
  adminEmailsSettings: '/api/admin/emails/settings',
  adminEmailSetting: (id: string) => `/api/admin/emails/settings/${id}`,
  adminEmailPreview: (template: string) => `/api/admin/emails/preview/${template}`,
  adminSessionRecording: (id: number) => `/api/admin/sessions/${id}/recording`,
  // Plan 85 — admin "Notify Client" sends one consolidated email
  // summarising the recording + booking_resources for a booking and
  // stamps `bookings.notified_at`.
  adminSessionNotifyClient: (id: number) => `/api/admin/sessions/${id}/notify-client`,
  // Admin calendar grid — bookings + GCal events inside a window. `from` and
  // `to` are ISO strings; defaults to today + 7 days on the server.
  adminCalendar: (from: string, to: string) =>
    `/api/admin/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  // Plan 71 — admin Past-tab reschedule/cancellation history per booking.
  // (Plan 71's adminBookingResources hook reuses the helper plan 70 already
  // added above — the URL shape is identical.)
  adminBookingHistory: (id: number) => `/api/admin/bookings/${id}/history`,
  // Plan 78 — admin contact-thread inbox. The list endpoint accepts the
  // filter set as querystring params (status, inquiry_type, unread,
  // existing_client, q, limit, offset); callers serialise via URLSearchParams
  // — no helper here so the per-call payload stays explicit.
  adminContactThreads: '/api/admin/contact-threads',
  adminContactThread: (id: number) => `/api/admin/contact-threads/${id}`,
  adminContactThreadNotes: (id: number) => `/api/admin/contact-threads/${id}/notes`,
  adminContactThreadReply: (id: number) => `/api/admin/contact-threads/${id}/reply`,
  // Migration 025 — Audio Report (CONTENT product) deliveries.
  // Plan 213 WS3 — paginated at 20/page; server defaults pageSize to 20.
  adminAudioReportsPending: (page = 1) => `/api/admin/audio-reports/pending?page=${page}`,
  adminAudioReportsDelivered: (page = 1) => `/api/admin/audio-reports/delivered?page=${page}`,
  adminAudioReportUpload: (id: number) => `/api/admin/audio-reports/${id}/upload`,
  adminAudioReportResendEmail: (id: number) => `/api/admin/audio-reports/${id}/resend-email`,
  // Plan 212 — mark a paid purchase fulfilled via its Builder order number.
  adminAudioReportLinkOrder: (id: number) => `/api/admin/audio-reports/${id}/link-order`,
  // Plan 122 WS4 — gift certificates ledger.
  adminGiftCertificates: (status: 'pending' | 'fulfilled', page = 1) =>
    `/api/admin/gift-certificates?status=${status}&page=${page}`,
  adminGiftCertificateFulfil: (id: number) => `/api/admin/gift-certificates/${id}/fulfil`,
  myAudioReports: '/api/audio-reports/me',
  devPromoteMe: '/api/dev/promote-me',
  devSeedTestData: '/api/dev/seed-test-data',
} as const;

/**
 * Plan 204 — Admin Audio Report Builder. NEW surface for the bespoke
 * audio-report generator; distinct from the legacy `adminAudioReport*`
 * keys above which operate on `purchases` rows.
 *
 * Mounted under `/api/admin/audio-report-builder`. The client (plan 208)
 * imports this namespace to compose `safeFetch` calls — never hardcode
 * the URLs at call sites.
 */
export const API_AUDIO_REPORT_BUILDER = {
  list: '/api/admin/audio-report-builder',
  create: '/api/admin/audio-report-builder',
  detail: (id: number) => `/api/admin/audio-report-builder/${id}`,
  update: (id: number) => `/api/admin/audio-report-builder/${id}`,
  generate: (id: number) => `/api/admin/audio-report-builder/${id}/generate`,
  approve: (id: number) => `/api/admin/audio-report-builder/${id}/approve`,
  sendDeliveryEmail: (id: number) => `/api/admin/audio-report-builder/${id}/send-delivery-email`,
  regenerate: (id: number) => `/api/admin/audio-report-builder/${id}/regenerate`,
  resendDeliveryEmail: (id: number) =>
    `/api/admin/audio-report-builder/${id}/resend-delivery-email`,
  cancel: (id: number) => `/api/admin/audio-report-builder/${id}/cancel`,
  previewManifest: (id: number) => `/api/admin/audio-report-builder/${id}/preview-manifest`,
  renderStatus: (id: number) => `/api/admin/audio-report-builder/${id}/render-status`,
  audioPreviewUrl: (id: number) => `/api/admin/audio-report-builder/${id}/audio-preview-url`,
  brandedUrl: (id: number) => `/api/admin/audio-report-builder/${id}/branded-url`,
} as const;
